// Access Policies panel — shown on a data source's detail page. Manages the
// data_access_policy recipe rows scoped to this source: row filters (SQL
// predicate templates over the data's columns with :subject.<attr>
// placeholders) and column masks. Enforced by the authz layer + engine read
// path; platform admins bypass. Includes a "preview as user" tool that shows
// the compiled constraints for any member of the project's org.
import React, { useEffect, useMemo, useState } from "react";
import { listPolicies, createPolicy, updatePolicy, deletePolicy, previewPolicies } from "../../services/accessPolicies";
import { ShieldCheck, Plus, X, Trash2, Pencil, Eye, Loader2 } from "lucide-react";
import { useTutorialDemo } from "../tutorial/useTutorialDemo";

const KIND_LABEL = { row_filter: "Row filter", column_mask: "Column mask" };

export default function AccessPoliciesPanel({ dataSourceId }) {
  const [policies, setPolicies] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | policy row
  const [previewOpen, setPreviewOpen] = useState(false);
  // Tutorial demo: open the policy modal for DISPLAY (sample values, no submit).
  const demo = useTutorialDemo(["add-policy"]);

  const load = async () => {
    setErr(null);
    try { setPolicies(await listPolicies(dataSourceId)); }
    catch (e) { setErr(e?.message || "Failed to load access policies"); setPolicies([]); }
  };
  useEffect(() => { load(); }, [dataSourceId]); // eslint-disable-line

  const toggle = async (p) => {
    try { await updatePolicy(p.id, { is_active: p.is_active ? 0 : 1 }); load(); }
    catch (e) { setErr(e?.message || "Toggle failed"); }
  };
  const remove = async (p) => {
    try { await deletePolicy(p.id); load(); }
    catch (e) { setErr(e?.message || "Delete failed"); }
  };

  return (
    <div className="bg-card-bg rounded-[8px] p-4 md:p-5" data-testid="access-policies-panel" data-tour="access-policies">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <ShieldCheck size={16} /> Access Policies
          <span className="text-xs font-normal text-page-muted">row/column entitlements applied when apps and analysts read this data</span>
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreviewOpen(true)}
            className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs hover:bg-page-bg">
            <Eye size={14} /> Preview as user
          </button>
          <button onClick={() => setEditing({})} data-testid="add-policy"
            className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] bg-nav-active text-white text-xs font-medium hover:bg-nav-active-dark transition-colors">
            <Plus size={14} /> Add policy
          </button>
        </div>
      </div>

      {err && <div className="bg-red-50 text-red-800 px-3 py-2 rounded text-sm mb-2">{err}</div>}

      {policies === null ? (
        <div className="text-sm text-page-muted py-3"><Loader2 className="inline animate-spin mr-2" size={14} />Loading…</div>
      ) : policies.length === 0 ? (
        <p className="text-sm text-page-muted py-2">
          No policies — every member of this project's organization sees all rows and columns from this source.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="py-2 pr-4 font-medium">Name</th>
              <th className="py-2 pr-4 font-medium">Kind</th>
              <th className="py-2 pr-4 font-medium">Definition</th>
              <th className="py-2 pr-4 font-medium">Roles</th>
              <th className="py-2 pr-4 font-medium">Active</th>
              <th className="py-2 w-[90px]"></th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-b border-page-divider last:border-b-0">
                <td className="py-2 pr-4 font-medium">{p.name}</td>
                <td className="py-2 pr-4"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{KIND_LABEL[p.kind] || p.kind}</span></td>
                <td className="py-2 pr-4 font-mono text-xs text-page-muted max-w-[380px] truncate" title={p.definition}>{p.definition}</td>
                <td className="py-2 pr-4 text-xs text-page-muted">{p.applies_to_roles || "all roles"}</td>
                <td className="py-2 pr-4">
                  <button onClick={() => toggle(p)}
                    className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>
                    {p.is_active ? "active" : "off"}
                  </button>
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-page-bg text-page-muted" title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <PolicyModal
          dataSourceId={dataSourceId}
          policy={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {previewOpen && <PreviewModal onClose={() => setPreviewOpen(false)} />}
      {demo === "add-policy" && (
        <PolicyModal tutorialDemo dataSourceId={dataSourceId} policy={null} onClose={() => {}} onSaved={() => {}} />
      )}
    </div>
  );
}

function ModalShell({ title, onClose, children, footer, tutorialDemo }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      {...(tutorialDemo ? { "data-tutorial-demo-modal": "1" } : {})}
    >
      <div className="bg-card-bg rounded-[8px] shadow-xl w-full max-w-lg mt-12 md:mt-0">
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-page-bg"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-page-divider">{footer}</div>}
      </div>
    </div>
  );
}

export function PolicyModal({ dataSourceId, policy, onClose, onSaved, tutorialDemo }) {
  const [name, setName] = useState(policy?.name || (tutorialDemo ? "Own region only" : ""));
  const [kind, setKind] = useState(policy?.kind || "row_filter");
  const [definition, setDefinition] = useState(policy?.definition || (tutorialDemo ? "region = :subject.region" : ""));
  const [roles, setRoles] = useState(policy?.applies_to_roles || (tutorialDemo ? "app_user, member" : ""));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const valid = useMemo(() => name.trim() && definition.trim(), [name, definition]);
  const submit = async () => {
    if (tutorialDemo) { onClose(); return; } // display-only in a tutorial
    if (!valid) return;
    setSubmitting(true); setErr(null);
    const body = {
      data_source_id: Number(dataSourceId),
      name: name.trim(),
      kind,
      definition: definition.trim(),
      applies_to_roles: roles.trim() || null,
      is_active: policy ? policy.is_active : 1,
    };
    try {
      if (policy) await updatePolicy(policy.id, body);
      else await createPolicy(body);
      onSaved();
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={policy ? `Edit policy — ${policy.name}` : "New access policy"} onClose={onClose} tutorialDemo={tutorialDemo}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={!valid || submitting} onClick={submit} data-testid="save-policy"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Saving…" : "Save policy"}
        </button>
      </>}>
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Name</span>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="policy-name"
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active" />
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="policy-kind"
            className="w-full h-9 px-2 rounded border border-page-divider bg-page-bg text-sm">
            <option value="row_filter">Row filter — users see only matching rows</option>
            <option value="column_mask">Column mask — hide columns from users</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">
            {kind === "row_filter"
              ? <>Predicate — SQL condition over this data's columns; <code className="font-mono text-xs">:subject.&lt;attr&gt;</code> is replaced per user</>
              : <>Definition — JSON: <code className="font-mono text-xs">{'{"columns": ["ssn"], "unless_attribute": "pii_reader"}'}</code></>}
          </span>
          <textarea value={definition} onChange={(e) => setDefinition(e.target.value)} rows={3} data-testid="policy-definition"
            placeholder={kind === "row_filter" ? "region = :subject.region" : '{"columns": ["salary", "ssn"], "unless_attribute": "pii_reader"}'}
            className="w-full px-3 py-2 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs" />
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Applies to roles <span className="text-xs">(CSV; empty = all. Platform admins always bypass)</span></span>
          <input value={roles} onChange={(e) => setRoles(e.target.value)} placeholder="app_user, member"
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs" />
        </label>
        {err && <div className="text-sm text-red-700">{err}</div>}
        <p className="text-xs text-page-muted">
          Subject attributes come from the member's attribute bag (Organizations → member → attributes) merged with the organization's external policy provider, if configured. A referenced attribute the user lacks denies the rows (fail closed).
        </p>
      </div>
    </ModalShell>
  );
}

function PreviewModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setErr(null); setResult(null);
    try { setResult(await previewPolicies(email.trim() || undefined)); }
    catch (e) { setErr(e?.message || "Preview failed"); }
    finally { setLoading(false); }
  };

  return (
    <ModalShell title="Preview effective access" onClose={onClose}
      footer={<button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Close</button>}>
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <label className="text-sm flex-1">
            <span className="block text-page-muted mb-1">User email (empty = you)</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs" />
          </label>
          <button onClick={run} disabled={loading}
            className="h-9 px-4 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
            {loading ? "Compiling…" : "Compile"}
          </button>
        </div>
        {err && <div className="text-sm text-red-700">{err}</div>}
        {result && (
          <pre className="bg-page-bg rounded p-3 text-xs font-mono overflow-x-auto max-h-[300px]" data-testid="preview-result">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>
    </ModalShell>
  );
}
